// Onboarding step 2a (create path, docs/06 §2): force acknowledgment of the
// recovery phrase before it's ever quizzed. The phrase itself is passed down
// from OnboardingFlow's in-memory identity state — never persisted here.

import { useRef, useState, type ReactNode } from 'react';
import { useT } from '../../i18n/index.js';

/** Long-press duration (ms) for the dev-build shortcut to skip the phrase quiz. */
const DEV_SKIP_HOLD_MS = 800;

export function ShowPhrase({
  words,
  onContinue,
  onBack,
  onDevSkip,
}: {
  words: string[];
  onContinue: () => void;
  /** Backs out to the welcome screen, forgetting the just-generated identity. */
  onBack?: () => void;
  /** Debug builds only (config.DEV_BUILD): hold Continue to skip the confirm quiz. */
  onDevSkip?: () => void;
}): ReactNode {
  const t = useT();
  const [acknowledged, setAcknowledged] = useState(false);
  const holdTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const held = useRef(false);

  function startHold(): void {
    if (!onDevSkip) return;
    held.current = false;
    holdTimer.current = setTimeout(() => {
      held.current = true;
      onDevSkip();
    }, DEV_SKIP_HOLD_MS);
  }

  function cancelHold(): void {
    if (holdTimer.current) clearTimeout(holdTimer.current);
  }

  return (
    <main className="mx-auto flex min-h-full w-full max-w-app flex-col gap-6 p-6">
      <h1 className="text-2xl font-semibold text-ink">{t('showPhrase.title')}</h1>
      <p className="text-sm text-muted">
        {t('showPhrase.intro1')}
        <strong className="text-ink">{t('showPhrase.only')}</strong>
        {t('showPhrase.intro2')}
      </p>
      <ol className="grid grid-cols-2 gap-x-4 gap-y-2 rounded-xl bg-surface p-4 sm:grid-cols-3">
        {words.map((word, i) => (
          <li key={i} className="flex items-baseline gap-2 text-sm">
            <span aria-hidden="true" className="w-5 text-right text-muted">
              {i + 1}.
            </span>
            <span data-testid={`phrase-word-${i + 1}`} className="select-text font-mono text-ink">
              {word}
            </span>
          </li>
        ))}
      </ol>
      <label className="flex min-h-touch items-center gap-3 text-sm text-ink">
        <input
          type="checkbox"
          checked={acknowledged}
          onChange={(e) => setAcknowledged(e.target.checked)}
          className="h-5 w-5 shrink-0"
        />
        {t('showPhrase.wroteDown')}
      </label>
      <div className="flex gap-3">
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            className="min-h-touch flex-1 rounded-xl border border-edge bg-surface px-4 font-medium text-ink"
          >
            {t('common.back')}
          </button>
        )}
        <button
          type="button"
          disabled={!acknowledged && !onDevSkip}
          onClick={() => {
            // A completed long-press already advanced via onDevSkip; don't also
            // fire the normal (quiz) path on the click that ends the hold.
            if (held.current) {
              held.current = false;
              return;
            }
            if (acknowledged) onContinue();
          }}
          onPointerDown={startHold}
          onPointerUp={cancelHold}
          onPointerLeave={cancelHold}
          className="min-h-touch flex-1 rounded-xl bg-accent px-4 font-medium text-ink disabled:opacity-40 active:bg-accent-press"
        >
          {t('common.continue')}
        </button>
      </div>
    </main>
  );
}
